import {QlQueryDto} from '../data/dto/ql-query.dto';
import {QlBuilderModel} from './ql-builder-model';
import {QlBuilderQueryConverter} from './ql-builder-query-converter';
import {FilterCriteria} from '../../filter/api/filter.criteria';
import {Sorting} from '../../misc/sorting';
import type {QlBuilderPresetResolver} from './interface/ql-builder-preset-resolver.interface';

/**
 * Mirrors UiQlWidgetType (core/ui/model/dto/api/ui-ql-widget-mapping.dto.ts) — duplicated as a
 * literal union here to keep this module free of any dependency on the core/ui layer.
 */
export type QlBuilderPresetWidgetTypeHint = 'table' | 'list' | 'chart' | 'category';

/**
 * Re-exported from its own file ({@link QlBuilderPresetResolver}) rather than redeclared here, so
 * every existing import of `ql-builder-presets` keeps working unchanged.
 */
export type {QlBuilderPresetResolver} from './interface/ql-builder-preset-resolver.interface';

export type QlBuilderPresetResult =
    | {
        status: 'available';
        id: string;
        title: string;
        widgetTypeHint: QlBuilderPresetWidgetTypeHint;
        /** null only for preset 8 — its whole point is a shape the builder model cannot represent. */
        model: QlBuilderModel | null;
        /** The request to run/precheck — for preset 8 this is preset 1's query plus the injected violation. */
        qlQuery: QlQueryDto;
    }
    | {
        status: 'unavailable';
        id: string;
        title: string;
        /** German, user-facing reason ("auf dieser Umgebung nicht verfuegbar: ..."). */
        reason: string;
    };

const PREVIEW_LIMIT = 25;

/**
 * Ordinals of the TERMINAL ticket statuses (`tm/logic/TmStatusEnum.java`, `active=false`):
 * `resolved` = 5 ("Abgeschlossen"), `cancelled` = 7 ("Abgebrochen"). Every other status (draft 0, assigned 1,
 * working 2, awaiting_customer 3, awaiting_partner 4, on_hold 6, awaiting_appointment 8, reopened 9) is
 * active and therefore "offen". Hardcoded here because `core/shared` must not import the tm feature's
 * `TmStatusEnum`; the ordinals are contractually stable (`AlphaBaseEnum`, `@Enumerated(ORDINAL)`).
 */
export const TM_TICKET_TERMINAL_STATUS_ORDINALS: ReadonlyArray<number> = [5, 7];

/**
 * Label (2nd, human-readable) field per entity used as a preset ROOT — verified against the Java
 * entities directly (none of these has a `name` column, so a hand-written `{name: 'name'}` reads
 * as plausible but fails at the QL engine with an unknown-field error):
 * - cr.Customer implements EntityCalculatedName; its persisted label column is `calculatedName`
 *   (Customer.java: `String calculatedName;` + `getName() { return getCalculatedName(); }` — `name`
 *   itself is only a computed getter, never a declared field). Confirmed against the production
 *   convention: existing QL usages join `customer` with `fields: [{name:'id'},
 *   {name:'calculatedName'}]` (e.g. bd-mahnwesen-ql.service.ts).
 * - cm.Contract has no `name`/calculated-name column at all (Contract.java) — its
 *   `deriveName()`/`deriveShortName()` overrides (Contract.java:795-803) are computed at runtime
 *   from other entities, not backed by a column, so they are equally unreachable from QL.
 *   `contractIdentifier` (the VSNR, Contract.java:378) is its established display field, also the
 *   one other QL/DTO code uses as the contract's label (e.g.
 *   bd-contract-decision-section.component.ts).
 * - xx.XxPerson has no persisted label column at all (XxPerson.java: only
 *   `firstName`/`lastName`, lines 51/58); `getName()` (line 81) composes them at runtime but is a
 *   Java method, not a QL-queryable field. `lastName` is the closest existing single label field.
 *
 * General rule this documents: QL field resolution only ever sees actually DECLARED JPA fields —
 * confirmed backend-side by EntityMeta.loadMetaData (core/entity/EntityMeta.java), which builds
 * the whole attribute list QL/meta expose from `ReflectionHelper.getDeclaredFieldsAll(entityClass)`
 * (reflection on the field, never the getter), and again by
 * QlDpEntityJdbcProviderImplResolver.checkMemberExists, which uses `Class#getDeclaredField` even
 * for entities that implement EntityNameGet and override `getName()`. So a computed
 * `getName()`/`deriveName()` override alone (as on cm.Contract, cr.Customer, xx.XxPerson) never
 * makes `name` itself QL-addressable.
 */
const ENTITY_LABEL_FIELD: Readonly<Record<string, string>> = {
    'cr.Customer': 'calculatedName',
    'cm.Contract': 'contractIdentifier',
    'xx.XxPerson': 'lastName',
};

/**
 * The 8 QL-Playground presets (requirement.md Playground). FE-only constants, no Java
 * counterpart, no ticket. Every preset resolves to a POLICY-VALID definition except #8, whose job
 * is to demonstrate exactly one rejection (forbiddenParameters) — see
 * QlBuilderQueryConverter.precheckQlQuery.
 */
export const QL_BUILDER_PRESETS: ReadonlyArray<{
    id: string;
    title: string;
    build: (resolver: QlBuilderPresetResolver) => QlBuilderPresetResult;
}> = [
    {
        id: 'preset-1-ticket-type-join',
        title: 'Tickets mit Typ (Tabelle)',
        build: (): QlBuilderPresetResult => buildFromModel(
            'preset-1-ticket-type-join',
            'Tickets mit Typ (Tabelle)',
            'table',
            {
                root: {
                    kind: 'entity',
                    alias: 'e',
                    entityName: 'tm.Ticket',
                    fields: [{name: 'id'}, {name: 'name'}, {name: 'status'}],
                    filters: [],
                    children: [
                        {
                            kind: 'join',
                            alias: 'j1',
                            name: 'type',
                            relation: 'nToOne',
                            joinType: 'left',
                            fields: [{name: 'id'}, {name: 'name'}],
                            filters: [],
                            children: [],
                        },
                    ],
                },
            },
        ),
    },
    {
        id: 'preset-2-ticket-status-category',
        title: 'Offene Tickets nach Status (Donut)',
        build: (): QlBuilderPresetResult => buildFromModel(
            'preset-2-ticket-status-category',
            'Offene Tickets nach Status (Donut)',
            'category',
            {
                root: {
                    kind: 'entity',
                    alias: 'e',
                    entityName: 'tm.Ticket',
                    fields: [],
                    // "Offen" = every status the backend flags active (TmStatusEnum.active): the two terminal
                    // ones are excluded by ORDINAL (enums are @Enumerated(ORDINAL), ordinals never change).
                    filters: [FilterCriteria.create('e.status', FilterCriteria.cOperatorInNot, TM_TICKET_TERMINAL_STATUS_ORDINALS)],
                    children: [],
                },
                groupBy: {
                    fields: [{name: 'status', as: 'status'}],
                    aggregates: [{name: '*', as: 'count', fn: 'count'}],
                },
            },
        ),
    },
    {
        id: 'preset-3-ticket-created-month-chart',
        title: 'Tickets pro Monat (Balken)',
        build: (): QlBuilderPresetResult => buildFromModel(
            'preset-3-ticket-created-month-chart',
            'Tickets pro Monat (Balken)',
            'chart',
            {
                root: {
                    kind: 'entity',
                    alias: 'e',
                    entityName: 'tm.Ticket',
                    fields: [],
                    filters: [],
                    children: [],
                },
                groupBy: {
                    fields: [{name: 'createdDate', as: 'createdMonth', dateGroup: {precision: 'month'}}],
                    aggregates: [{name: '*', as: 'count', fn: 'count'}],
                },
                sortings: [new Sorting('e.createdMonth', true)],
            },
        ),
    },
    {
        id: 'preset-4-customer-contacts-only-first',
        title: 'Kunden mit erstem Kontakt (Tabelle)',
        build: (): QlBuilderPresetResult => buildFromModel(
            'preset-4-customer-contacts-only-first',
            'Kunden mit erstem Kontakt (Tabelle)',
            'table',
            {
                root: {
                    kind: 'entity',
                    alias: 'e',
                    entityName: 'cr.Customer',
                    fields: [{name: 'id'}, {name: ENTITY_LABEL_FIELD['cr.Customer']}],
                    filters: [],
                    children: [
                        {
                            kind: 'join',
                            alias: 'j1',
                            name: 'cr.CustomerContact#customer',
                            relation: 'oneToN',
                            joinType: 'left',
                            onlyFirst: true,
                            // no join sort (spike S5: DISTINCT ON vs ORDER BY); the back-reference
                            // FK customerId is added by QlBuilderQueryConverter.toQlQuery
                            fields: [{name: 'id'}, {name: 'preferredContact'}],
                            filters: [],
                            children: [],
                        },
                    ],
                },
            },
        ),
    },
    {
        id: 'preset-5-generic-field',
        title: 'Datensätze mit ausgefülltem Zusatzfeld (Tabelle)',
        build: (resolver): QlBuilderPresetResult => {
            const entityAlias = 'xx.XxGenericTest';
            const fieldId = resolver.resolveGenericFieldId(entityAlias);
            if (fieldId == null) {
                return unavailable(
                    'preset-5-generic-field',
                    'Datensätze mit ausgefülltem Zusatzfeld (Tabelle)',
                    `Kein generisches Feld auf '${entityAlias}' auf dieser Umgebung gefunden.`,
                );
            }

            const fieldName = `g_${fieldId}:value`;
            return buildFromModel(
                'preset-5-generic-field',
                'Datensätze mit ausgefülltem Zusatzfeld (Tabelle)',
                'table',
                {
                    root: {
                        kind: 'entity',
                        alias: 'e',
                        entityName: entityAlias,
                        fields: [{name: 'id'}, {name: 'name'}, {name: fieldName}],
                        filters: [FilterCriteria.create(`e.${fieldName}`, FilterCriteria.cOperatorIsNotNull)],
                        children: [],
                    },
                },
            );
        },
    },
    {
        id: 'preset-6-extended-contract',
        title: 'Verträge mit Agenturnummer',
        build: (): QlBuilderPresetResult => buildFromModel(
            'preset-6-extended-contract',
            'Verträge mit Agenturnummer',
            'table',
            {
                root: {
                    kind: 'entity',
                    alias: 'e',
                    entityName: 'cm.Contract',
                    fields: [{name: 'id'}, {name: ENTITY_LABEL_FIELD['cm.Contract']}],
                    filters: [],
                    children: [
                        // spike S2: exactly ONE +/_ node, an attribute (a + join is not renderable),
                        // never combined with a side-car
                        {
                            kind: 'extended',
                            alias: 'j1',
                            name: 'agencyNumber',
                            extendedKind: 'service',
                            resultKind: 'attribute',
                        },
                    ],
                },
            },
        ),
    },
    {
        id: 'preset-7-sidecar',
        title: 'Datensätze mit externen Zusatzfeldern (Tabelle)',
        build: (resolver): QlBuilderPresetResult => {
            // requirement.md Playground names 'ci.Contract' as the dev-environment fallback host,
            // but that entity does not exist — ci.ContractExtData's verified host FK is cm.Contract
            // (ContractExtData.java:143, `import com.mvs.backend.cm.model.Contract`). Built against
            // the code, per this task's verification mandate, not the doc's (evidently stale) text.
            const resolved = resolver.resolveSideCar([
                {hostAlias: 'xx.XxPerson', instanceAlias: 'xx.XxPersonExternalFlat'},
                {hostAlias: 'cm.Contract', instanceAlias: 'ci.ContractExtData'},
            ]);
            if (!resolved) {
                return unavailable(
                    'preset-7-sidecar',
                    'Datensätze mit externen Zusatzfeldern (Tabelle)',
                    'Kein Seitenwagen mit Version und Feldern auf dieser Umgebung gefunden '
                    + '(weder xx.XxPerson/XxPersonExternalFlat noch cm.Contract/ci.ContractExtData).',
                );
            }

            // Neither candidate host has a `name` attribute — see ENTITY_LABEL_FIELD's doc comment
            // above for the per-entity evidence. The host is only known after resolution (either
            // candidate may win), so the label field is looked up by the RESOLVED alias rather than
            // hardcoded; QlBuilderPresetResolver.resolveSideCar (./interface/ql-builder-preset-
            // resolver.interface.ts) only answers side-car discovery, so this stays a static map
            // here rather than a resolver extension.
            const hostLabelField = ENTITY_LABEL_FIELD[resolved.hostAlias];
            const rootFields = hostLabelField
                ? [{name: 'id'}, {name: hostLabelField}]
                : [{name: 'id'}];

            return buildFromModel(
                'preset-7-sidecar',
                'Datensätze mit externen Zusatzfeldern (Tabelle)',
                'table',
                {
                    root: {
                        kind: 'entity',
                        alias: 'e',
                        entityName: resolved.hostAlias,
                        fields: rootFields,
                        filters: [],
                        children: [
                            {
                                kind: 'sideCar',
                                alias: 'sc',
                                instanceAlias: resolved.instanceAlias,
                                scopeKey: resolved.scopeKey,
                                scopeId: resolved.scopeId,
                                typeFieldIds: resolved.typeFieldIds,
                            },
                        ],
                    },
                },
            );
        },
    },
    {
        id: 'preset-8-negative-forbidden-parameters',
        title: 'Negativbeispiel: nicht erlaubter Parameter (Regelverstoß)',
        build: (resolver): QlBuilderPresetResult => {
            const base = QL_BUILDER_PRESETS[0].build(resolver);
            if (base.status !== 'available') {
                // Preset 1 is fully static and always available; this branch is unreachable in
                // practice but kept so the type system does not need a non-null assertion.
                return unavailable(
                    'preset-8-negative-forbidden-parameters',
                    'Negativbeispiel: nicht erlaubter Parameter (Regelverstoß)',
                    'Basis-Preset (#1) ist nicht verfügbar.',
                );
            }

            // 'parameters' cannot be expressed by QlBuilderModel at all (by design — see
            // ql-builder-model.ts doc comment), so this preset injects it directly into the
            // QlQueryDto, exactly as the Playground's JSON editor would ("nur im JSON-Reiter
            // moeglich" — requirement.md Playground point 8).
            const qlQuery: QlQueryDto = JSON.parse(JSON.stringify(base.qlQuery));
            qlQuery.start!.parameters = [{param: 'NO_AUTH_CHECK', value: 'true'}];

            return {
                status: 'available',
                id: 'preset-8-negative-forbidden-parameters',
                title: 'Negativbeispiel: nicht erlaubter Parameter (Regelverstoß)',
                widgetTypeHint: 'table',
                model: null,
                qlQuery,
            };
        },
    },
];

function buildFromModel(
    id: string,
    title: string,
    widgetTypeHint: QlBuilderPresetWidgetTypeHint,
    partialModel: Pick<QlBuilderModel, 'root'> & Partial<Omit<QlBuilderModel, 'root'>>,
): QlBuilderPresetResult {

    const model: QlBuilderModel = {
        limit: PREVIEW_LIMIT,
        ...partialModel,
    };

    return {
        status: 'available',
        id,
        title,
        widgetTypeHint,
        model,
        qlQuery: QlBuilderQueryConverter.toQlQuery(model),
    };
}

function unavailable(id: string, title: string, reason: string): QlBuilderPresetResult {
    return {status: 'unavailable', id, title, reason};
}

/** Resolve every preset in order against one resolver — the shape the Playground's dropdown consumes. */
export function resolveAllQlBuilderPresets(resolver: QlBuilderPresetResolver): QlBuilderPresetResult[] {
    return QL_BUILDER_PRESETS.map(p => p.build(resolver));
}
